import { startOfDay, isAfter } from 'date-fns';
import type { Campaign, CampaignKanbanStage } from '../types/campaign.types';

export const COLUMN_CONFIGS = [
  { id: 'discovery' as const, title: 'Discovery', color: 'bg-yellow-50 border-yellow-200' },
  { id: 'in_progress' as const, title: 'In Progress', color: 'bg-purple-50 border-purple-200' },
  { id: 'completed' as const, title: 'Completed', color: 'bg-green-50 border-green-200' },
];

export const getStatusForStage = (kanban_stage: string): 'draft' | 'active' | 'completed' => {
  switch (kanban_stage) {
    case 'discovery':
      return 'draft';
    case 'in_progress':
      return 'active';
    case 'completed':
      return 'completed';
    default:
      return 'draft';
  }
};

export const resolveCampaignStatusAndProgress = (
  startDateStr?: string | null,
  endDateStr?: string | null,
  rawStatus?: string | null
): { status: string; progress: number } => {
  const statusClean = (rawStatus || 'draft').trim().toLowerCase();

  const start = startDateStr ? new Date(startDateStr) : null;
  const end = endDateStr ? new Date(endDateStr) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let rawProgress = 0;
  if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
    const totalDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    const startZero = new Date(start);
    startZero.setHours(0, 0, 0, 0);
    const endZero = new Date(end);
    endZero.setHours(0, 0, 0, 0);

    if (today >= endZero) {
      rawProgress = 100;
    } else if (today < startZero) {
      rawProgress = 0;
    } else {
      const completedDays = Math.max(1, Math.floor((today.getTime() - startZero.getTime()) / (1000 * 60 * 60 * 24)) + 1);
      rawProgress = Math.min(99, Math.max(1, Math.round((completedDays / totalDays) * 100)));
    }
  }

  let progress = 0;
  let derivedStatus = statusClean;

  if (statusClean === 'completed' || rawProgress >= 100) {
    progress = 100;
    derivedStatus = 'completed';
  } else if (statusClean === 'paused' || statusClean === 'cancelled') {
    derivedStatus = statusClean;
    progress = rawProgress;
  } else if (statusClean === 'active' || (rawProgress > 0 && rawProgress < 100)) {
    progress = Math.min(Math.max(rawProgress, 1), 99);
    derivedStatus = 'active';
  } else {
    progress = 0;
    derivedStatus = 'draft';
  }

  return { status: derivedStatus, progress };
};

export const calculateCampaignProgress = (
  startDateStr?: string | null,
  endDateStr?: string | null,
  status?: string | null
): number => {
  return resolveCampaignStatusAndProgress(startDateStr, endDateStr, status).progress;
};

export const isCampaignStarted = (campaign: Campaign): boolean => {
  if (campaign.status?.trim().toLowerCase() === 'completed') {
    return false;
  }

  if (campaign.status?.trim().toLowerCase() === 'active') {
    return true;
  }

  const startDateValue = campaign.start_date;
  if (!startDateValue) {
    return false;
  }

  const startDate = new Date(startDateValue);
  if (Number.isNaN(startDate.getTime())) {
    return false;
  }

  return !isAfter(startOfDay(startDate), startOfDay(new Date()));
};

export const groupCampaignsForKanban = (
  campaigns: Campaign[]
): Record<CampaignKanbanStage, Campaign[]> => {
  const groups: Record<CampaignKanbanStage, Campaign[]> = {
    discovery: [],
    in_progress: [],
    completed: [],
  };

  campaigns.forEach((c) => {
    let stage: CampaignKanbanStage = (c.kanban_stage === 'discovery' || c.kanban_stage === 'in_progress' || c.kanban_stage === 'completed')
      ? c.kanban_stage
      : 'discovery';

    const statusClean = c.status?.trim().toLowerCase() || '';
    const started = isCampaignStarted(c);

    const isMismatched =
      (stage === 'discovery' && (statusClean === 'active' || started)) ||
      (stage === 'in_progress' && !started && statusClean !== 'completed') ||
      (stage === 'completed' && statusClean !== 'completed');

    if (isMismatched) {
      if (statusClean === 'completed') {
        stage = 'completed';
      } else if (started || statusClean === 'active') {
        stage = 'in_progress';
      } else {
        stage = 'discovery';
      }
    }

    groups[stage].push(c);
  });

  return groups;
};
